import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Platform, Text, View, ViewStyle } from 'react-native';
import { ActivityIndicator, Button } from 'react-native-paper';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RADIUS, TYPOGRAPHY } from '../assets/theme';
import { makeStyles, useAppTheme } from '../theme/ThemeContext';
import { useLayout } from '../theme/useLayout';
import { Badge, Card, FilterChip, SectionHeader, Screen, TextInput } from '../components/ui';
import EmptyState from '../components/EmptyState';
import { RootStackParamList } from '../navigation/AppNavigator';
import { commandeService } from '../services/commandeService';
import { vehiculeService } from '../services/vehiculeService';
import { clientService } from '../services/clientService';
import { abonnementService } from '../services/abonnementService';
import { produitServiceService } from '../services/produitService';
import {
  Client,
  Commande,
  getServiceLabel,
  ModeAbonnementScreen,
  ProduitService,
  ServiceLigneRequest,
  TypeAbonnement,
  Vehicule,
} from '../types';

type RouteType = RouteProp<RootStackParamList, 'Abonnement'>;
type NavType = StackNavigationProp<RootStackParamList, 'Abonnement'>;

/** Keeps the summary card visible while scrolling the form (web desktop only). */
const STICKY_WEB = { position: 'sticky', top: 24 } as unknown as ViewStyle;

const SERVICE_CODES = ['*B', '*A'];
const CODES_HISTORIQUE_ABONNEMENT = ['*A'];
const SELECTION_PREMIER = ['*B'];
const SELECTION_RENOUVELLEMENT = ['*A'];

function normalizeCode(code?: string): string {
  return (code ?? '').trim().toUpperCase();
}

function formatDateFr(dateValue?: string): string {
  if (!dateValue) return '-';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return dateValue;
  return date.toLocaleDateString('fr-FR');
}

function commandeCouvreVehicule(commande: Commande, vehiculeId: number): boolean {
  if (commande.vehicule?.id === vehiculeId) {
    return true;
  }
  return (commande.vehiculesConcernes ?? []).some((v) => v.id === vehiculeId);
}

function hasHistoryForVehicule(commandes: Commande[], vehiculeId: number): boolean {
  return commandes.some((commande) => {
    if (!commandeCouvreVehicule(commande, vehiculeId)) {
      return false;
    }

    return commande.lignesCommande.some((ligne) => {
      const code = (ligne.produitService?.code ?? '').trim().toUpperCase();
      return CODES_HISTORIQUE_ABONNEMENT.includes(code);
    });
  });
}

function prixDepuisCatalogue(produitsParCode: Map<string, ProduitService>, code: string): string {
  const produit = produitsParCode.get(code);
  const prix = produit ? Number(produit.prixUnitaire) : NaN;
  return Number.isFinite(prix) && prix > 0 ? prix.toFixed(2) : '';
}

export default function AbonnementScreen() {
  const route = useRoute<RouteType>();
  const navigation = useNavigation<NavType>();
  const styles = useStyles();
  const { colors } = useAppTheme();
  const { isDesktop, isWide } = useLayout();
  const { vehiculeId, clientId, vehiculeIds, servicesPreselectionnes } = route.params;
  const mode: ModeAbonnementScreen = route.params.mode ?? 'VEHICULE';
  const estModeGroupe = mode !== 'VEHICULE';

  const [vehicule, setVehicule] = useState<Vehicule | null>(null);
  const [clientGroupe, setClientGroupe] = useState<Client | null>(null);
  const [produitsServices, setProduitsServices] = useState<ProduitService[]>([]);
  const [typeAbonnement, setTypeAbonnement] = useState<TypeAbonnement>('PREMIER');
  const [codesSelectionnes, setCodesSelectionnes] = useState<string[]>([]);
  const [prixParCode, setPrixParCode] = useState<Record<string, string>>({});
  const [detailBas, setDetailBas] = useState('');
  const [dureeAbonnementMois, setDureeAbonnementMois] = useState('12');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const nbVehiculesConcernes = estModeGroupe ? (vehiculeIds?.length ?? 0) : 1;

  const produitsParCode = useMemo(() => {
    const map = new Map<string, ProduitService>();
    produitsServices.forEach((produit) => {
      map.set(normalizeCode(produit.code), produit);
    });
    return map;
  }, [produitsServices]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const produitsCatalog = await produitServiceService.getAll();
      const catalogParCode = new Map<string, ProduitService>();
      produitsCatalog.forEach((produit) => {
        catalogParCode.set(normalizeCode(produit.code), produit);
      });

      let selectionInitiale: string[];
      if (estModeGroupe) {
        if (!clientId) {
          throw new Error('Le client est manquant pour ce document groupé.');
        }
        const clientData = await clientService.getById(clientId);
        setClientGroupe(clientData);
        selectionInitiale = (servicesPreselectionnes ?? [])
          .map(normalizeCode)
          .filter((code) => SERVICE_CODES.includes(code));
      } else {
        if (!vehiculeId) {
          throw new Error('Le véhicule est manquant.');
        }
        const vehiculeData = await vehiculeService.getById(vehiculeId);
        if (!vehiculeData?.client?.id) {
          throw new Error("Le client lié au véhicule est introuvable.");
        }

        const commandesClient = await commandeService.getByClient(vehiculeData.client.id);
        const historiqueAbonnement = hasHistoryForVehicule(commandesClient, vehiculeId);
        const estRenouvellement = historiqueAbonnement
          || vehiculeData.statut === 'ACTIF'
          || !!vehiculeData.dateExpirationAbonnement;
        const typeParDefaut: TypeAbonnement = estRenouvellement ? 'RENOUVELLEMENT' : 'PREMIER';

        setVehicule(vehiculeData);
        setTypeAbonnement(typeParDefaut);
        selectionInitiale = estRenouvellement ? SELECTION_RENOUVELLEMENT : SELECTION_PREMIER;
      }

      const prixInitiaux: Record<string, string> = {};
      selectionInitiale.forEach((code) => {
        prixInitiaux[code] = prixDepuisCatalogue(catalogParCode, code);
      });
      setProduitsServices(produitsCatalog);
      setCodesSelectionnes(selectionInitiale);
      setPrixParCode(prixInitiaux);
      setDetailBas(selectionInitiale.includes('*B') ? (catalogParCode.get('*B')?.detail ?? '') : '');
    } catch (e: any) {
      const message = e?.response?.data?.erreur
        ?? e?.message
        ?? "Impossible de charger les informations d'abonnement.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [estModeGroupe, clientId, vehiculeId, servicesPreselectionnes]);

  useEffect(() => {
    load();
  }, [load]);

  const clientActuel = estModeGroupe ? clientGroupe : vehicule?.client ?? null;

  const documentLabel = useMemo(() => {
    if (estModeGroupe) {
      return clientGroupe?.type === 'PARTICULIER' ? 'Bon de commande' : 'Devis (B2B)';
    }
    if (vehicule?.client?.type === 'PARTICULIER') {
      return 'Bon de commande';
    }
    return 'Commande en attente (B2B)';
  }, [estModeGroupe, clientGroupe, vehicule]);

  const appliquerSelection = useCallback((codes: string[]) => {
    const valides = codes.map(normalizeCode).filter((code) => SERVICE_CODES.includes(code));
    setCodesSelectionnes(valides);
    setPrixParCode((prev) => {
      const next = { ...prev };
      valides.forEach((code) => {
        if (next[code] === undefined) {
          next[code] = prixDepuisCatalogue(produitsParCode, code);
        }
      });
      return next;
    });
    if (valides.includes('*B')) {
      setDetailBas((prev) => prev || (produitsParCode.get('*B')?.detail ?? ''));
    }
  }, [produitsParCode]);

  const toggleService = useCallback((code: string) => {
    setSubmitError(null);
    const codeNormalise = normalizeCode(code);
    if (codesSelectionnes.includes(codeNormalise)) {
      setCodesSelectionnes(codesSelectionnes.filter((c) => c !== codeNormalise));
      return;
    }
    appliquerSelection([...codesSelectionnes, codeNormalise]);
  }, [appliquerSelection, codesSelectionnes]);

  const handleSelectTypeAbonnement = useCallback((type: TypeAbonnement) => {
    setSubmitError(null);
    setTypeAbonnement(type);
    appliquerSelection(type === 'RENOUVELLEMENT' ? SELECTION_RENOUVELLEMENT : SELECTION_PREMIER);
  }, [appliquerSelection]);

  const retourProfilClient = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();
      return;
    }
    const id = vehicule?.client?.id ?? clientGroupe?.id ?? clientId;
    if (id) {
      navigation.navigate('ProfilClient', { clientId: id });
    }
  }, [navigation, vehicule, clientGroupe, clientId]);

  const handleSubmit = async () => {
    setSubmitError(null);
    if (codesSelectionnes.length === 0) {
      setSubmitError('Veuillez sélectionner au moins un service.');
      return;
    }

    const contientAbonnement = codesSelectionnes.some((code) => CODES_HISTORIQUE_ABONNEMENT.includes(code));
    const duree = Number(dureeAbonnementMois);
    if (contientAbonnement && (!Number.isInteger(duree) || duree <= 0)) {
      setSubmitError("La durée de l'abonnement doit être un nombre entier supérieur à 0.");
      return;
    }

    const services: ServiceLigneRequest[] = [];
    for (const code of codesSelectionnes) {
      const prix = Number((prixParCode[code] ?? '').replace(',', '.'));
      if (!Number.isFinite(prix) || prix <= 0) {
        setSubmitError(`Le prix unitaire du service ${code} doit être un montant supérieur à 0.`);
        return;
      }
      services.push({
        code,
        prixUnitaire: Number(prix.toFixed(2)),
        quantite: nbVehiculesConcernes,
        detail: code === '*B' && detailBas.trim() ? detailBas.trim() : undefined,
      });
    }

    setSubmitting(true);
    try {
      let message: string;
      if (estModeGroupe) {
        const response = await abonnementService.createForClient(clientId as number, {
          services,
          dureeAbonnementMois: contientAbonnement ? duree : undefined,
          vehiculeIds: vehiculeIds ?? [],
        });
        message = response.message;
      } else {
        const response = await abonnementService.createForVehicule((vehicule as Vehicule).id, {
          dureeAbonnementMois: duree,
          services,
        });
        message = response.message;
      }

      if (Platform.OS === 'web') {
        retourProfilClient();
        return;
      }

      Alert.alert('Succès', message, [
        {
          text: 'OK',
          onPress: retourProfilClient,
        },
      ]);
    } catch (e: any) {
      const message = e?.response?.data?.erreur
        ?? e?.message
        ?? "Impossible de générer le document d'abonnement.";
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator color={colors.primary} />
        <Text style={styles.loadingText}>Chargement de l'abonnement...</Text>
      </View>
    );
  }

  if (error || (estModeGroupe ? !clientGroupe : !vehicule)) {
    return (
      <View style={styles.center}>
        <EmptyState
          icon="alert-circle-outline"
          message={error ?? (estModeGroupe ? 'Client introuvable.' : 'Véhicule introuvable.')}
          action={(
            <Button mode="contained" onPress={load}>
              Réessayer
            </Button>
          )}
        />
      </View>
    );
  }

  const estParticulier = clientActuel?.type === 'PARTICULIER';
  const titrePage = estModeGroupe
    ? (mode === 'INSTALLATION'
      ? (estParticulier ? "Bon de commande d'installation" : "Devis d'installation")
      : (estParticulier ? 'Bon de commande de renouvellement' : 'Devis de renouvellement'))
    : 'Gestion Abonnement';
  const sousTitre = estModeGroupe
    ? `${clientGroupe?.nomRaisonSociale ?? ''} - ${nbVehiculesConcernes} véhicule(s) concerné(s)`
    : `Véhicule #${vehicule?.id} - ${vehicule?.immatriculation ?? ''}`;
  const servicesAbsents = codesSelectionnes.filter((code) => !produitsParCode.has(code));
  const afficherDuree = codesSelectionnes.some((code) => CODES_HISTORIQUE_ABONNEMENT.includes(code));

  // Affichage uniquement : estimation du montant à partir des prix saisis (le document est calculé par le serveur).
  const prixAffiche = (code: string): number => {
    const prix = Number((prixParCode[code] ?? '').replace(',', '.'));
    return Number.isFinite(prix) && prix > 0 ? prix : 0;
  };
  const totalEstime = codesSelectionnes.reduce((total, code) => total + prixAffiche(code) * nbVehiculesConcernes, 0);
  const formatMontant = (montant: number) => `${montant.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH`;
  const etapeServices = estModeGroupe ? 2 : 3;

  // Erreur + actions : dans la carte récapitulative sur desktop, sous la carte sur mobile (pleine largeur).
  const ctaBlock = (
    <>
      {submitError ? (
        <View style={styles.submitErrorBox}>
          <MaterialCommunityIcons name="alert-circle-outline" size={18} color={colors.danger} />
          <Text style={styles.submitErrorText}>{submitError}</Text>
        </View>
      ) : null}

      <Button
        mode="contained"
        onPress={handleSubmit}
        loading={submitting}
        disabled={submitting || codesSelectionnes.length === 0}
        buttonColor={colors.primary}
        textColor={colors.onPrimary}
        style={styles.submitBtn}
        contentStyle={styles.submitBtnContent}
        labelStyle={styles.submitBtnLabel}
      >
        {estModeGroupe
          ? (mode === 'INSTALLATION'
            ? (estParticulier ? "Créer le bon de commande d'installation" : "Créer le devis d'installation")
            : (estParticulier ? 'Créer le bon de commande de renouvellement' : 'Créer le devis de renouvellement'))
          : (estParticulier
            ? 'Créer le bon de commande'
            : "Créer la commande d'abonnement")}
      </Button>

      {!estModeGroupe && (
        <Button
          mode="outlined"
          icon="skip-next-outline"
          onPress={retourProfilClient}
          disabled={submitting}
          style={styles.skipButton}
          textColor={colors.textPrimary}
        >
          Passer l'ajout de l'abonnement
        </Button>
      )}
    </>
  );

  // ── Récapitulatif (colonne de droite sur desktop, fin de page sur mobile) ──
  const summaryCard = (
    <Card raised style={[styles.summaryCard, isDesktop && Platform.OS === 'web' ? STICKY_WEB : null]}>
      <Text style={styles.summaryTitle}>Récapitulatif</Text>

      <View style={styles.kvList}>
        {codesSelectionnes.length === 0 ? (
          <Text style={styles.summaryEmpty}>Aucun service sélectionné.</Text>
        ) : (
          codesSelectionnes.map((code) => (
            <View key={code} style={styles.kvRow}>
              <Text style={styles.kvLabel} numberOfLines={1}>
                {getServiceLabel(code)} × {nbVehiculesConcernes}
              </Text>
              <Text style={styles.kvValue}>{formatMontant(prixAffiche(code) * nbVehiculesConcernes)}</Text>
            </View>
          ))
        )}
        {afficherDuree && (
          <View style={styles.kvRow}>
            <Text style={styles.kvLabel}>Durée</Text>
            <Text style={styles.kvValue}>{dureeAbonnementMois ? `${dureeAbonnementMois} mois` : '—'}</Text>
          </View>
        )}
      </View>

      <View style={styles.totalRow}>
        <Text style={styles.totalLabel}>Total HT estimé</Text>
        <Text style={styles.totalValue}>{formatMontant(totalEstime)}</Text>
      </View>

      {isDesktop ? ctaBlock : null}
    </Card>
  );

  return (
    <Screen>
      <View style={[styles.layout, isDesktop && styles.layoutDesktop]}>
        <View style={[styles.formColumn, isDesktop && styles.formColumnDesktop]}>
          <View style={styles.intro}>
            <Badge label={documentLabel} tone={estParticulier ? 'brand' : 'warning'} dot />
            <Text style={styles.title}>{titrePage}</Text>
            <Text style={styles.subtitle}>{sousTitre}</Text>
          </View>

          {/* ── Étape 1 : informations client / véhicule ── */}
          <StepCard step={1} title={estModeGroupe ? 'Informations Client' : 'Informations Véhicule'}>
            <View style={styles.infoRow}>
              <Text style={styles.label}>Client</Text>
              <Text style={styles.value}>{clientActuel?.nomRaisonSociale ?? '-'}</Text>
            </View>
            <View style={[styles.infoRow, styles.infoRowDivider]}>
              <Text style={styles.label}>Type client</Text>
              <Text style={styles.value}>{clientActuel?.type === 'PARTICULIER' ? 'Particulier' : 'Agence'}</Text>
            </View>
            {estModeGroupe ? (
              <View style={[styles.infoRow, styles.infoRowDivider]}>
                <Text style={styles.label}>Véhicules concernés</Text>
                <Text style={styles.value}>{nbVehiculesConcernes}</Text>
              </View>
            ) : (
              <View style={[styles.infoRow, styles.infoRowDivider]}>
                <Text style={styles.label}>Expiration actuelle</Text>
                <Text style={styles.value}>{formatDateFr(vehicule?.dateExpirationAbonnement)}</Text>
              </View>
            )}
          </StepCard>

          {/* ── Étape 2 : type d'abonnement (mode véhicule) ── */}
          {!estModeGroupe && (
            <StepCard
              step={2}
              title="Type d'abonnement"
              subtitle="Le choix présélectionne les services correspondants."
            >
              <View style={styles.chipsRow}>
                <FilterChip
                  label="Premier abonnement"
                  selected={typeAbonnement === 'PREMIER'}
                  onPress={() => handleSelectTypeAbonnement('PREMIER')}
                />
                <FilterChip
                  label="Renouvellement"
                  selected={typeAbonnement === 'RENOUVELLEMENT'}
                  onPress={() => handleSelectTypeAbonnement('RENOUVELLEMENT')}
                />
              </View>
            </StepCard>
          )}

          {/* ── Étape : services ── */}
          <StepCard
            step={etapeServices}
            title="Services"
            subtitle={`Services sélectionnés (${codesSelectionnes.length})`}
          >
            <View style={styles.chipsRow}>
              {SERVICE_CODES.map((code) => {
                const selected = codesSelectionnes.includes(code);
                const configure = produitsParCode.has(code);
                return (
                  <FilterChip
                    key={code}
                    label={`${code} - ${getServiceLabel(code)}${configure ? '' : ' ✦ nouveau'}`}
                    icon={selected ? 'check' : 'plus'}
                    selected={selected}
                    onPress={() => toggleService(code)}
                  />
                );
              })}
            </View>

            {servicesAbsents.length > 0 && (
              <View style={styles.infoNote}>
                <MaterialCommunityIcons name="information-outline" size={18} color={colors.info} />
                <Text style={styles.infoNoteText}>
                  Services absents du catalogue, ajout automatique avec le prix saisi: {servicesAbsents.join(', ')}
                </Text>
              </View>
            )}

            {codesSelectionnes.length === 0 ? (
              <Text style={styles.emptySelectionText}>Sélectionnez un ou plusieurs services ci-dessus.</Text>
            ) : (
              <View style={styles.priceList}>
                {codesSelectionnes.map((code, index) => (
                  <View key={code} style={[styles.servicePriceRow, index > 0 && styles.servicePriceRowDivider]}>
                    <View style={styles.servicePriceInfo}>
                      <Text style={styles.servicePriceLabel}>{code} - {getServiceLabel(code)}</Text>
                      {estModeGroupe && (
                        <Text style={styles.servicePriceQty}>Quantité: {nbVehiculesConcernes} véhicule(s)</Text>
                      )}
                    </View>
                    <TextInput
                      label="Prix unitaire (DH) *"
                      value={prixParCode[code] ?? ''}
                      onChangeText={(value) => {
                        setSubmitError(null);
                        setPrixParCode((prev) => ({ ...prev, [code]: value.replace(/[^\d.,]/g, '') }));
                      }}
                      keyboardType="decimal-pad"
                      dense
                      style={styles.servicePriceInput}
                    />
                  </View>
                ))}
              </View>
            )}

            {codesSelectionnes.includes('*B') && (
              <TextInput
                label="Détail du service *B"
                value={detailBas}
                onChangeText={(value) => { setSubmitError(null); setDetailBas(value); }}
                multiline
                numberOfLines={4}
                style={styles.detailInput}
              />
            )}
          </StepCard>

          {/* ── Étape : durée d'abonnement ── */}
          {afficherDuree && (
            <StepCard
              step={etapeServices + 1}
              title="Durée d'abonnement"
              subtitle="Nombre de mois couverts par l'abonnement."
            >
              <TextInput
                label="Durée d'abonnement (mois) *"
                value={dureeAbonnementMois}
                onChangeText={(value) => setDureeAbonnementMois(value.replace(/\D/g, ''))}
                keyboardType="number-pad"
              />
              <View style={[styles.chipsRow, styles.quickDurations]}>
                {[1, 3, 6, 12].map((mois) => (
                  <FilterChip
                    key={mois}
                    label={`${mois} mois`}
                    selected={dureeAbonnementMois === String(mois)}
                    onPress={() => setDureeAbonnementMois(String(mois))}
                  />
                ))}
              </View>
            </StepCard>
          )}
        </View>

        <View style={isDesktop ? [styles.summaryColumn, { width: isWide ? 400 : 360 }] : null}>
          {summaryCard}
          {isDesktop ? null : ctaBlock}
        </View>
      </View>
    </Screen>
  );
}

/** Numbered form step: card + step number + section title. */
function StepCard({
  step,
  title,
  subtitle,
  children,
}: {
  step: number;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  const styles = useStyles();
  return (
    <Card>
      <View style={styles.stepHeader}>
        <View style={styles.stepNumber}>
          <Text style={styles.stepNumberText}>{step}</Text>
        </View>
        <SectionHeader title={title} subtitle={subtitle} style={styles.stepTitle} />
      </View>
      {children}
    </Card>
  );
}

const useStyles = makeStyles((c) => ({
  // States
  center: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    backgroundColor: c.background,
  },
  loadingText: { marginTop: 12, fontSize: 14, color: c.textSecondary },
  // Layout
  layout: { gap: 20 },
  layoutDesktop: { flexDirection: 'row', alignItems: 'stretch', gap: 24 },
  formColumn: { gap: 20 },
  formColumnDesktop: { flex: 1, minWidth: 0 },
  summaryColumn: { flexShrink: 0 },
  // Intro
  intro: { gap: 6, alignItems: 'flex-start' },
  title: { ...TYPOGRAPHY.title, color: c.textPrimary, marginTop: 4 },
  subtitle: { ...TYPOGRAPHY.body, color: c.textSecondary },
  // Steps
  stepHeader: { flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 16 },
  stepNumber: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: c.surfaceAlt,
    borderWidth: 1,
    borderColor: c.divider,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepNumberText: { ...TYPOGRAPHY.label, color: c.textPrimary },
  stepTitle: { flex: 1, marginBottom: 0 },
  // Key-value rows
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    gap: 12,
  },
  infoRowDivider: { borderTopWidth: 1, borderTopColor: c.divider },
  label: { fontSize: 14, color: c.textSecondary },
  value: { flex: 1, textAlign: 'right', fontSize: 15, color: c.textPrimary, fontWeight: '600' },
  // Chips
  chipsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  quickDurations: { marginTop: 12 },
  // Services
  infoNote: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    marginTop: 16,
    padding: 12,
    borderRadius: RADIUS.md,
    backgroundColor: c.infoSoft,
  },
  infoNoteText: { flex: 1, fontSize: 13, lineHeight: 18, color: c.info, fontWeight: '500' },
  emptySelectionText: { fontSize: 14, color: c.textSecondary, marginTop: 16 },
  priceList: { marginTop: 8 },
  servicePriceRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 12,
  },
  servicePriceRowDivider: { borderTopWidth: 1, borderTopColor: c.divider },
  servicePriceInfo: { flexGrow: 1, flexShrink: 1, flexBasis: 160, minWidth: 0 },
  servicePriceLabel: { ...TYPOGRAPHY.bodyStrong, color: c.textPrimary },
  servicePriceQty: { fontSize: 13, color: c.textSecondary, marginTop: 2 },
  servicePriceInput: { width: 180 },
  detailInput: { marginTop: 12 },
  // Summary
  summaryCard: { padding: 24 },
  summaryTitle: { ...TYPOGRAPHY.title, color: c.textPrimary, marginBottom: 16 },
  kvList: { gap: 10, paddingBottom: 16, borderBottomWidth: 1, borderBottomColor: c.divider },
  kvRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 12 },
  kvLabel: { flexShrink: 1, fontSize: 14, color: c.textSecondary },
  kvValue: { textAlign: 'right', fontSize: 15, fontWeight: '600', color: c.textPrimary },
  summaryEmpty: { fontSize: 14, lineHeight: 20, color: c.textSecondary },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    paddingTop: 16,
  },
  totalLabel: { ...TYPOGRAPHY.subheading, fontWeight: '700', color: c.textPrimary },
  totalValue: { ...TYPOGRAPHY.heading, color: c.textPrimary },
  submitErrorBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    marginTop: 16,
    padding: 12,
    borderRadius: RADIUS.md,
    backgroundColor: c.dangerSoft,
  },
  submitErrorText: { flex: 1, color: c.danger, fontSize: 13, lineHeight: 18, fontWeight: '600' },
  submitBtn: { marginTop: 20, borderRadius: RADIUS.md },
  submitBtnContent: { paddingVertical: 8 },
  // Paper renders button labels on one line: keep the default size and trim side margins so long labels fit.
  submitBtnLabel: { fontWeight: '700', marginHorizontal: 12 },
  skipButton: { marginTop: 10, borderRadius: RADIUS.md, borderColor: c.border },
}));
